import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useCallback } from 'react';

import { type UUID } from '~/types/common';

import {
  fetchPartnerCompanies,
  fetchPartnerCompany,
  type PartnerCompaniesQueryParams,
  type PartnerCompany,
  queryKeysDataExchange,
} from '~/data/dataExchange';
import { useGetUserUtils } from '~/data/user';

import { type Option, SelectServerDriven } from './SelectServerDriven';
import { createGetItemData, createLoadOptions } from './utils';

const getOptionLabel = ({ name }: PartnerCompany) => name;

type P = {
  readonly companyAccountId?: PartnerCompaniesQueryParams['companyAccountId'];
  readonly exchangeDlns?: PartnerCompaniesQueryParams['exchangeDlns'];
  readonly formatOptionLabel?: (option: Option<PartnerCompany>) => ReactNode;
  readonly includeOwn?: PartnerCompaniesQueryParams['includeOwn'];
  readonly isClearable?: boolean;
  readonly isDisabled?: boolean;
  readonly isMultiSelect?: boolean;
  readonly isRemovableValue?: (data: Option<PartnerCompany>) => boolean;
  readonly onChange: (value: Array<{ id: UUID }> | { id: UUID }) => void;
  readonly placeholder?: string;
  readonly value: UUID | UUID[];
} & ComponentStyling;

export const MultiSelectSuppliers = ({
  className,
  companyAccountId,
  exchangeDlns,
  formatOptionLabel,
  includeOwn,
  isClearable = true,
  isDisabled = false,
  isMultiSelect = true,
  isRemovableValue,
  placeholder = 'Lieferanten',
  onChange,
  style,
  value,
}: P) => {
  const queryClient = useQueryClient();
  const { UserUtils } = useGetUserUtils();

  /**
   * Loads the options for the multi-select dropdown.
   * @param {string} searchString - The search string entered by the user.
   * @param {unknown} loadedOptions - The already loaded options.
   * @param {{ page: number }} - The current page number.
   */
  const loadOptions = createLoadOptions<PartnerCompany>({
    createQueryKey: ({ offset, searchString }) =>
      queryKeysDataExchange.getPartnerCompanies({
        companyAccountId,
        exchangeDlns,
        includeOwn,
        offset,
        searchString,
      }),
    async fetchItems({ offset, searchString }) {
      const { data, hasNextPage, paginatedCount } = await fetchPartnerCompanies(
        {
          companyAccountId,
          exchangeDlns,
          includeOwn,
          offset,
          searchString: searchString || undefined,
        },
      );
      return { data, hasNextPage, count: paginatedCount };
    },
    queryClient,
  });

  /**
   * Gets company data for a company with a given companyId.
   * Attempts to get it from the query cache and fetches it from the API
   * if the company data is not in the cache.
   * @param {string} companyId - The ID of the company to get.
   */
  const getItemData = createGetItemData<PartnerCompany>({
    createQueryKey: (companyId) => [
      ...queryKeysDataExchange.getPartnerCompany(companyId),
    ],
    fetchItem: fetchPartnerCompany,
    queryClient,
    queryKeyBase: [queryKeysDataExchange.base(), 'partnerCompanies'],
  });

  const handleChange = useCallback(
    (newValues: string[] | string) => {
      onChange(
        Array.isArray(newValues)
          ? newValues.map((id) => ({ id }))
          : { id: newValues },
      );
    },
    [onChange],
  );

  return (
    <SelectServerDriven<PartnerCompany>
      className={className}
      formatOptionLabel={formatOptionLabel}
      getItemData={getItemData}
      getOptionLabel={getOptionLabel}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isItemSelectionAllowed={UserUtils.isCompanyReadAllowedUser}
      isMultiSelect={isMultiSelect}
      isRemovableValue={isRemovableValue}
      loadOptions={loadOptions}
      placeholder={placeholder}
      style={style}
      value={value}
      onChange={handleChange}
    />
  );
};
